import React from 'react';
import { capitalize } from '../../utils/helpers';
import FilterControls from './FilterControls';
import SortIndicator from './SortIndicator';
import { useActiveModules } from '../../hooks/useActiveModules';

const StudentTable = ({
  studentSearch,
  setStudentSearch,
  completedModuleFilters,
  setCompletedModuleFilters,
  showDropdown,
  setShowDropdown,
  dropdownRef,
  sortConfig,
  handleStudentSelection,
  setSelectedStudent,
  handleSort,
  onDownloadCSV,
  getFilteredAndSortedStudents
}) => {
  const { activeModules, activeModulesIds, activeModulesLoading, activeModulesError} = useActiveModules();


  if (activeModulesLoading) {
    return <div>Loading modules...</div>;
  }

  return (
    <div className="module-summary-table-container">
      {/* Download csv button */} 
      <div style={{ textAlign: 'right', marginBottom: '10px' }}>
        <span
          onClick={onDownloadCSV}
          style={{
            padding: '6px 12px',
            color: '#405159',
            display: 'inline-block',
            cursor: 'pointer',
            textDecoration: 'none',
            fontSize: '16px',
            fontWeight: 'bold'
          }}
          onMouseEnter={e => (e.currentTarget.style.textDecoration = 'underline')}
          onMouseLeave={e => (e.currentTarget.style.textDecoration = 'none')}
        >
          📥 Download Table as CSV
        </span>
      </div>

      {/* Filter container */}
      <FilterControls
        studentSearch={studentSearch}
        setStudentSearch={setStudentSearch}
        completedModuleFilters={completedModuleFilters}
        setCompletedModuleFilters={setCompletedModuleFilters}
        showDropdown={showDropdown}
        setShowDropdown={setShowDropdown}
        dropdownRef={dropdownRef}
        setSelectedStudent={setSelectedStudent}
      />

      {/* Table wrapper */}
      <div className="module-summary-scroll-wrapper">
        <table className="module-summary-table">
          <thead>
            <tr>
              <th className="sticky-col" rowSpan={2} style={{ cursor: 'pointer', padding: 0 }}>
                <button
                  className="sort-button"
                  onClick={() => handleSort(null, 'student_name')}
                  style={{ width: '100%', height: '100%', padding: '8px 0', color: "#405159" }}
                >
                  Student Name{' '}
                  <SortIndicator
                    isActive={sortConfig.metric === 'student_name'}
                    direction={sortConfig.direction}
                  />
                </button>
              </th>

              {/* New header cell for total completed passages */}
              <th className="sticky-col-total" rowSpan={2}>
                <button
                  className="sort-button"
                  onClick={() => handleSort(null, 'total_completed_passages')}
                  style={{ width: '100%', height: '100%', padding: '8px 0', color: "#405159" }}
                >
                  Dialogues Passed{' '}
                  <SortIndicator
                    isActive={sortConfig.metric === 'total_completed_passages'}
                    direction={sortConfig.direction}
                  />
                </button>
              </th>

              {/* New header cell for total failed passages */}
              <th className="sticky-col-total" rowSpan={2}>
                <button
                  className="sort-button"
                  onClick={() => handleSort(null, 'total_failed_passages')}
                  style={{ width: '100%', height: '100%', padding: '8px 0', color: "#405159" }}
                >
                  Dialogues Failed{' '}
                  <SortIndicator
                    isActive={sortConfig.metric === 'total_failed_passages'}
                    direction={sortConfig.direction}
                  />
                </button>
              </th>

              {activeModules.map((module) => (
                <th
                  key={module.module_id}
                  colSpan={3}
                  className={`module-header ${module.module_id === 8 ? 'module-separator' : ''}`}
                >
                  Module {module.module_id}: {module.module_name}
                </th>
              ))}
            </tr>
            <tr>
              {activeModules.flatMap((module) => [
                <th key={`c-${module.module_id}`}>
                  <button
                    className="sort-button"
                    onClick={() => handleSort(module.module_id, 'begun')}
                    style={{ color: "#405159" }}
                  >
                    Begun{' '}
                    <SortIndicator
                      isActive={sortConfig.moduleId === module.module_id && sortConfig.metric === 'begun'}
                      direction={sortConfig.direction}
                    />
                  </button>
                </th>,
                <th key={`a-${module.module_id}`}>
                  <button
                    className="sort-button"
                    onClick={() => handleSort(module.module_id, 'passed')}
                    style={{ color: "#405159" }}
                  >
                    Passed{' '}
                    <SortIndicator
                      isActive={sortConfig.moduleId === module.module_id && sortConfig.metric === 'passed'}
                      direction={sortConfig.direction}
                    />
                  </button>
                </th>,
                <th key={`m-${module.module_id}`} className="module-separator">
                  <button
                    className="sort-button"
                    onClick={() => handleSort(module.module_id, 'failed')}
                    style={{ color: "#405159" }}
                  >
                    Failed{' '}
                    <SortIndicator
                      isActive={sortConfig.moduleId === module.module_id && sortConfig.metric === 'failed'}
                      direction={sortConfig.direction}
                    />
                  </button>
                </th>,
              ])}
            </tr>
          </thead>
          <tbody>
            {getFilteredAndSortedStudents().map(([studentId, student]) => (
              <tr key={studentId}>
                <td
                  className="sticky-col clickable-student"
                  onClick={() => handleStudentSelection(studentId)} 
                >
                  {capitalize(student.student_last_name)}, {capitalize(student.student_first_name)}
                </td>

                <td className="centered-cell">
                  {student.total_completed_passages ?? 0}
                </td>

                <td className="centered-cell">
                  {student.total_failed_passages ?? 0}
                </td>

                {activeModules.flatMap((module) => [
                  <td key={`c-${studentId}-${module.module_id}`} className="centered-cell">
                    {student.num_attempted_passages?.[module.module_id] ?? 0}
                  </td>,
                  <td key={`a-${studentId}-${module.module_id}`} className="centered-cell">
                    {student.num_completed_passages?.[module.module_id] ?? 0}
                  </td>,
                  <td key={`m-${studentId}-${module.module_id}`} className="centered-cell module-separator">
                    {student.num_failed_passages?.[module.module_id] ?? 0}
                  </td>,
                ])}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default StudentTable;


